import React, { useEffect, useState } from "react";
import axiosInstance from "../../utils/axiosInstance";
import {
  ArrowDownIcon,
  ArrowsUpDownIcon,
  ArrowUpIcon,
  ArrowUturnLeftIcon,
  BoltIcon,
} from "@heroicons/react/24/outline";
import {
  Badge,
  Page,
  PageHeader,
  PageLoader,
  Pagination,
  Panel,
  StatTile,
  TableShell,
  Td,
  TdEmpty,
  Th,
  Tr,
} from "../../components/ui";

// Where a credit came from, in the order the ledger totals are reported —
// the same breakdown the customer sees on their own Credits Ledger.
const SOURCES = [
  { key: "admin", label: "from Admin", tone: "success", icon: ArrowsUpDownIcon },
  { key: "refund", label: "from Refund", tone: "info", icon: ArrowUturnLeftIcon },
  { key: "paypal", label: "from PayPal", tone: "success", icon: ArrowDownIcon },
  { key: "upload", label: "from Upload", tone: "danger", icon: ArrowUpIcon },
  { key: "automatic", label: "from Automatic", tone: "accent", icon: BoltIcon },
];

const CreditsLedger = () => {
  const [response, setResponse] = useState();
  const [credits, setCredits] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  const itemsPerPage = 35;

  useEffect(() => {
    const fetchCredits = async () => {
      setLoading(true);
      try {
        const offset = (currentPage - 1) * itemsPerPage;
        const response = await axiosInstance.get(
          `/credits-ledger?limit=${itemsPerPage}&offset=${offset}`
        );
        setCredits(response.data.credits_ledger);

        // Calculate the total number of pages
        const totalRows = response.data.total_rows;
        setTotalPages(Math.ceil(totalRows / itemsPerPage));
        setResponse(response.data);
      } catch (error) {
        console.error("Error fetching data", error);
      }
      setLoading(false);
    };
    fetchCredits();
  }, [currentPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <Page>
      <PageHeader title="Credits Ledger" onBack={() => window.history.back()} />

      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {SOURCES.map(({ key, label, tone, icon: Icon }) => (
          <StatTile
            key={key}
            label={`total ${label}`}
            value={loading ? "…" : response?.[key] ?? 0}
            tone={tone}
            icon={<Icon className="h-6 w-6" />}
          />
        ))}
      </div>

      {loading ? (
        <PageLoader label="Loading the ledger…" />
      ) : (
        <Panel padded={false}>
          <TableShell className="rounded-[22px] border-0">
            <thead>
              <tr>
                <Th>Username</Th>
                <Th>Credit Balance</Th>
                <Th>Source</Th>
                <Th>Created At</Th>
              </tr>
            </thead>
            <tbody>
              {credits.length === 0 ? <TdEmpty colSpan={4}>No credit movements yet.</TdEmpty> : null}
              {credits.map((credit) => (
                <Tr key={credit.id}>
                  <Td className="font-medium text-gray-800 dark:text-slate-200">
                    {credit.username}
                  </Td>
                  <Td
                    className={`font-bold ${
                      credit.credit_balance >= 0
                        ? "text-emerald-600 dark:text-emerald-400"
                        : "text-rose-600 dark:text-rose-400"
                    }`}
                  >
                    {credit.credit_balance >= 0 ? `+${credit.credit_balance}` : credit.credit_balance}
                  </Td>
                  <Td>
                    <Badge tone="neutral">{credit.source}</Badge>
                  </Td>
                  <Td className="whitespace-nowrap text-xs">
                    {new Date(credit.created_at).toLocaleString()}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </TableShell>
        </Panel>
      )}

      {/* Paged by the server (limit/offset), so this only drives the request. */}
      <Pagination
        page={currentPage}
        totalPages={totalPages}
        onChange={handlePageChange}
        total={response?.total_rows}
        pageSize={itemsPerPage}
      />
    </Page>
  );
};

export default CreditsLedger;
